import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    useIsLoggedIn,
    useIsPlayingAsGuest,
    useUsername,
    useUserRole,
    usePlayer,
} from '../../hooks';
import { actions } from '../../redux';
import { generateGuestUUID } from '../../api';
import { useSocket } from '../../socket';
import { acceptFriendRequest, rejectFriendRequest } from '../../api/profile';
import { FriendRequestToast, FriendAcceptedToast, ChallengeToast, ChallengeDeclinedToast, ChallengeErrorToast } from '../friend-request-toast';
import './style.css';

const NavigationSideBar = () => {
    const [userOpen, setUserOpen]         = useState(false);
    const [drawerOpen, setDrawerOpen]     = useState(false);
    const [toasts, setToasts]             = useState([]); // { id, type, from/by/timeControl }
    const userRef = useRef(null);

    const navigate   = useNavigate();
    const location   = useLocation();
    const dispatch   = useDispatch();
    const socket     = useSocket();

    const isLoggedIn = useIsLoggedIn();
    const isGuest    = useIsPlayingAsGuest();
    const username   = useUsername();
    const userRole   = useUserRole();
    const player     = usePlayer();

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    // Close dropdowns on outside click
    useEffect(() => {
        const handler = (e) => {
            if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Close dropdowns on route change
    useEffect(() => {
        setUserOpen(false);
        setDrawerOpen(false);
    }, [location.pathname]);

    // Listen for real-time friend request / accepted + challenge notifications
    useEffect(() => {
        if (!socket || !isLoggedIn || isGuest) return;

        const handleRequest = ({ from }) => {
            setToasts(prev => [...prev, { id: Date.now(), type: 'request', from }]);
        };
        const handleAccepted = ({ by }) => {
            setToasts(prev => [...prev, { id: Date.now(), type: 'accepted', by }]);
        };
        const handleChallengeReceived = ({ from, timeControl }) => {
            setToasts(prev => [...prev, { id: Date.now(), type: 'challenge', from, timeControl }]);
        };
        const handleChallengeDeclined = ({ by }) => {
            setToasts(prev => [...prev, { id: Date.now(), type: 'challengeDeclined', by }]);
        };
        const handleChallengeError = ({ message }) => {
            setToasts(prev => [...prev, { id: Date.now(), type: 'challengeError', message }]);
        };

        socket.on('friendRequestReceived', handleRequest);
        socket.on('friendRequestAccepted', handleAccepted);
        socket.on('challengeReceived', handleChallengeReceived);
        socket.on('challengeDeclined', handleChallengeDeclined);
        socket.on('challengeError', handleChallengeError);
        return () => {
            socket.off('friendRequestReceived', handleRequest);
            socket.off('friendRequestAccepted', handleAccepted);
            socket.off('challengeReceived', handleChallengeReceived);
            socket.off('challengeDeclined', handleChallengeDeclined);
            socket.off('challengeError', handleChallengeError);
        };
    }, [socket, isLoggedIn, isGuest]);

    const handleAccept = async (toast) => {
        removeToast(toast.id);
        try {
            await acceptFriendRequest(username, toast.from);
        } catch (err) {
            console.error('Failed to accept friend request:', err);
        }
    };

    const handleDecline = async (toast) => {
        removeToast(toast.id);
        try {
            await rejectFriendRequest(username, toast.from);
        } catch (err) {
            console.error('Failed to decline friend request:', err);
        }
    };

    const handleAcceptChallenge = (toast) => {
        removeToast(toast.id);
        socket.emit('acceptChallenge', { from: toast.from, timeControl: toast.timeControl });
    };

    const handleDeclineChallenge = (toast) => {
        removeToast(toast.id);
        socket.emit('declineChallenge', { from: toast.from }); // backend expects 'from' (the challenger)
    };

    const isActive = (path) => location.pathname === path;

    const go = (path) => () => navigate(path);

    const handlePlayAsGuest = async () => {
        try {
            // Reuse existing guest ID so in-progress games survive navigation
            let guestId = localStorage.getItem('guestPlayerId');
            if (!guestId) {
                guestId = await generateGuestUUID();
                localStorage.setItem('guestPlayerId', guestId);
            }
            dispatch(actions.playAsGuest(guestId));
            socket.emit('authenticate', { playerId: guestId });
            navigate('/');
        } catch (e) {
            console.error('Failed to initialize guest session:', e);
        } finally {
            setUserOpen(false);
        }
    };

    const handleLogout = () => {
        dispatch(actions.logOut());
        setUserOpen(false);
        navigate('/');
    };

    const userLabel = isLoggedIn && !isGuest ? username
        : isGuest ? 'Guest'
        : 'Sign in';

    const friendRequestBadge = toasts.filter(t => t.type === 'request').length;

    return (
        <>
            <nav className="topnav">
                {/* Brand */}
                <div className="topnav-brand" onClick={go('/')}>
                    <img src="/landmine_logo.png" alt="Landmine Chess" className="topnav-logo" />
                    <span className="topnav-wordmark">LANDMINE CHESS</span>
                </div>

                {/* Nav links */}
                <div className="topnav-links">
                    <button
                        className={`nav-link${isActive('/') ? ' nav-link--active' : ''}`}
                        onClick={go('/')}
                    >
                        Home
                    </button>

                    {/* Play — navigates to home where matchmaking lives */}
                    <button
                        className="nav-link"
                        onClick={go('/')}
                    >
                        Play
                    </button>

                    <button
                        className={`nav-link${isActive('/search') ? ' nav-link--active' : ''}`}
                        onClick={go('/search')}
                    >
                        Search
                    </button>

                    {isLoggedIn && !isGuest && (
                        <button
                            className={`nav-link${isActive(`/profile/${username}`) ? ' nav-link--active' : ''}`}
                            onClick={go(`/profile/${username}`)}
                        >
                            Profile
                            {friendRequestBadge > 0 && (
                                <span className="nav-badge">{friendRequestBadge}</span>
                            )}
                        </button>
                    )}

                    {isLoggedIn && !isGuest && (userRole === 'admin' || userRole === 'mod') && (
                        <button
                            className={`nav-link${isActive('/admin') ? ' nav-link--active' : ''}`}
                            onClick={go('/admin')}
                        >
                            Admin
                        </button>
                    )}
                </div>

                {/* User menu */}
                <div className="nav-dropdown-wrap nav-user" ref={userRef}>
                    <button
                        className={`nav-user-btn${userOpen ? ' open' : ''}`}
                        onClick={() => setUserOpen(v => !v)}
                    >
                        <span className="nav-user-avatar">
                            {isLoggedIn ? (isGuest ? 'G' : username.charAt(0).toUpperCase()) : '?'}
                        </span>
                        <span className="nav-user-label">{userLabel}</span>
                        <span className={`nav-chevron${userOpen ? ' open' : ''}`}>▾</span>
                    </button>

                    {userOpen && (
                        <div className="nav-dropdown nav-dropdown--right">
                            {!isLoggedIn ? (
                                <>
                                    <button className="nav-dropdown-item" onClick={() => { navigate('/sign-in'); setUserOpen(false); }}>
                                        <span className="nav-dropdown-icon">🔐</span>
                                        <span><strong>Sign In</strong></span>
                                    </button>
                                    <div className="nav-dropdown-divider" />
                                    <button className="nav-dropdown-item" onClick={() => { navigate('/create-account'); setUserOpen(false); }}>
                                        <span className="nav-dropdown-icon">✨</span>
                                        <span><strong>Create Account</strong></span>
                                    </button>
                                    <div className="nav-dropdown-divider" />
                                    <button className="nav-dropdown-item" onClick={handlePlayAsGuest}>
                                        <span className="nav-dropdown-icon">👾</span>
                                        <span><strong>Play as Guest</strong></span>
                                    </button>
                                </>
                            ) : isGuest ? (
                                <>
                                    <button className="nav-dropdown-item" onClick={() => { navigate('/sign-in'); setUserOpen(false); }}>
                                        <span className="nav-dropdown-icon">🔐</span>
                                        <span><strong>Sign In</strong></span>
                                    </button>
                                    <div className="nav-dropdown-divider" />
                                    <button className="nav-dropdown-item" onClick={() => { navigate('/create-account'); setUserOpen(false); }}>
                                        <span className="nav-dropdown-icon">✨</span>
                                        <span><strong>Create Account</strong></span>
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button className="nav-dropdown-item" onClick={() => { navigate(`/profile/${username}`); setUserOpen(false); }}>
                                        <span className="nav-dropdown-icon">👤</span>
                                        <span><strong>Profile</strong></span>
                                    </button>
                                    <div className="nav-dropdown-divider" />
                                    <button className="nav-dropdown-item nav-dropdown-item--danger" onClick={handleLogout}>
                                        <span className="nav-dropdown-icon">↩</span>
                                        <span><strong>Sign Out</strong></span>
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Hamburger — mobile only, shown via CSS */}
                <button
                    className="topnav-hamburger"
                    onClick={() => setDrawerOpen(v => !v)}
                    aria-label="Menu"
                    aria-expanded={drawerOpen}
                >
                    <span />
                    <span />
                    <span />
                </button>
            </nav>

            {/* Mobile hamburger drawer */}
            {drawerOpen && (
                <>
                    <div
                        className="mobile-drawer-backdrop"
                        onClick={() => setDrawerOpen(false)}
                    />
                    <div className="mobile-drawer">
                        {(isLoggedIn && !isGuest) && (
                            <div className="mobile-drawer-user">
                                <div className="mobile-drawer-avatar">
                                    {username.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <div className="mobile-drawer-username">{username}</div>
                                    {player?.rating != null && (
                                        <div className="mobile-drawer-elo">{player.rating} ELO</div>
                                    )}
                                </div>
                            </div>
                        )}

                        <button className={`mobile-drawer-item${isActive('/') ? ' mobile-drawer-item--active' : ''}`}
                            onClick={() => { go('/')(); setDrawerOpen(false); }}>
                            Home
                        </button>
                        <button className="mobile-drawer-item"
                            onClick={() => { go('/')(); setDrawerOpen(false); }}>
                            Play
                        </button>
                        <button className={`mobile-drawer-item${isActive('/search') ? ' mobile-drawer-item--active' : ''}`}
                            onClick={() => { go('/search')(); setDrawerOpen(false); }}>
                            Search
                        </button>
                        {isLoggedIn && !isGuest && (
                            <button className={`mobile-drawer-item${isActive(`/profile/${username}`) ? ' mobile-drawer-item--active' : ''}`}
                                onClick={() => { go(`/profile/${username}`)(); setDrawerOpen(false); }}>
                                Profile
                                {friendRequestBadge > 0 && (
                                    <span className="nav-badge">{friendRequestBadge}</span>
                                )}
                            </button>
                        )}
                        {isLoggedIn && !isGuest && (userRole === 'admin' || userRole === 'mod') && (
                            <button className={`mobile-drawer-item${isActive('/admin') ? ' mobile-drawer-item--active' : ''}`}
                                onClick={() => { go('/admin')(); setDrawerOpen(false); }}>
                                Admin
                            </button>
                        )}

                        <div className="mobile-drawer-divider" />

                        {!isLoggedIn ? (
                            <>
                                <button className="mobile-drawer-item" onClick={() => { navigate('/sign-in'); setDrawerOpen(false); }}>
                                    Sign In
                                </button>
                                <button className="mobile-drawer-item" onClick={() => { navigate('/create-account'); setDrawerOpen(false); }}>
                                    Create Account
                                </button>
                                <button className="mobile-drawer-item" onClick={() => { handlePlayAsGuest(); setDrawerOpen(false); }}>
                                    Play as Guest
                                </button>
                            </>
                        ) : isGuest ? (
                            <>
                                <button className="mobile-drawer-item" onClick={() => { navigate('/sign-in'); setDrawerOpen(false); }}>
                                    Sign In
                                </button>
                                <button className="mobile-drawer-item" onClick={() => { navigate('/create-account'); setDrawerOpen(false); }}>
                                    Create Account
                                </button>
                            </>
                        ) : (
                            <button className="mobile-drawer-item mobile-drawer-item--danger" onClick={() => { handleLogout(); setDrawerOpen(false); }}>
                                Sign Out
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* All toasts */}
            {toasts.length > 0 && (
                <div className="fr-toast-container">
                    {toasts.map(toast => {
                if (toast.type === 'request') {
                    return (
                        <FriendRequestToast
                            key={toast.id}
                            from={toast.from}
                            onAccept={() => handleAccept(toast)}
                            onDecline={() => handleDecline(toast)}
                            onClose={() => removeToast(toast.id)}
                        />
                    );
                }
                if (toast.type === 'accepted') {
                    return (
                        <FriendAcceptedToast
                            key={toast.id}
                            by={toast.by}
                            onClose={() => removeToast(toast.id)}
                        />
                    );
                }
                if (toast.type === 'challenge') {
                    return (
                        <ChallengeToast
                            key={toast.id}
                            from={toast.from}
                            timeControl={toast.timeControl}
                            onAccept={() => handleAcceptChallenge(toast)}
                            onDecline={() => handleDeclineChallenge(toast)}
                            onClose={() => removeToast(toast.id)}
                        />
                    );
                }
                if (toast.type === 'challengeDeclined') {
                    return (
                        <ChallengeDeclinedToast
                            key={toast.id}
                            by={toast.by}
                            onClose={() => removeToast(toast.id)}
                        />
                    );
                }
                if (toast.type === 'challengeError') {
                    return (
                        <ChallengeErrorToast
                            key={toast.id}
                            message={toast.message}
                            onClose={() => removeToast(toast.id)}
                        />
                    );
                }
                    return null;
                })}
                </div>
            )}

        </>
    );
};

export default NavigationSideBar;
